/**
 * Per-section headcount (T1, T2, B1, B2 + conductor/musicians when relevant) with how many have not
 * answered yet; warns when a singing section is thin.
 */
import { AlertTriangle } from 'lucide-react';
import { SECTIONS, SINGING_SECTIONS, type Section } from '../../../shared/audience';
import type { EventDTO, HeadcountDTO } from '../../../shared/dto/events';
import { useHeadcount } from '../../api/calendar';
import { ApiFailure } from '../../api/client';
import { SECTION_COLOR } from '../../components/people';
import { Card, Spinner } from '../../components/ui';
import { useT } from '../../i18n';
import { sectionGroupKey } from './shared';

type Counts = HeadcountDTO['bySection'][string];
const EMPTY: Counts = { yes: 0, no: 0, maybe: 0, none: 0, total: 0 };
const LOW_THRESHOLD = 2;

/**
 * Sections shown for this event, in choir order. The server only reports counted sections (singing
 * sections the event targets; conductor/musicians only when targeted explicitly).
 */
export function headcountSections(hc: HeadcountDTO): Section[] {
  return SECTIONS.filter((s) => hc.bySection[s] !== undefined);
}

/**
 * A section is thin when at most LOW_THRESHOLD of its members are coming or have yet to answer:
 * members answer for themselves, so silence alone (every event starts unanswered) is no warning.
 */
export function isLowSection(c: Counts): boolean {
  return c.yes + c.none <= LOW_THRESHOLD;
}

export function HeadcountCard({ ev }: { ev: EventDTO }) {
  const t = useT();
  const q = useHeadcount(ev.id);
  // Headcount may be editor-only on the server: hide quietly when not allowed.
  if (q.error instanceof ApiFailure && (q.error.status === 403 || q.error.status === 404)) return null;
  if (!q.data) {
    if (q.isError || q.fetchStatus === 'paused') return null;
    return (
      <Card className="flex min-h-24 items-center justify-center p-5">
        <Spinner />
      </Card>
    );
  }
  const hc = q.data;
  const sections = headcountSections(hc);
  const totalYes = sections.reduce((n, s) => n + (hc.bySection[s]?.yes ?? 0), 0);
  const totalNone = sections.reduce((n, s) => n + (hc.bySection[s]?.none ?? 0), 0);
  const low = ev.status === 'cancelled' ? [] : sections.filter((s) => (SINGING_SECTIONS as readonly string[]).includes(s) && isLowSection(hc.bySection[s] ?? EMPTY));

  return (
    <Card as="section" className="flex flex-col gap-4 p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-[20px] font-bold">{t('calendar.headcount.title')}</h2>
        <span className="text-[17px] font-semibold text-[var(--ink-2)]">
          {t('calendar.headcount.total')}: <span className="tabular-nums">{t('calendar.headcount.coming', { count: totalYes })}</span>
          {totalNone > 0 ? (
            <span className="text-[var(--accent)] tabular-nums"> · {t('calendar.headcount.unanswered', { count: totalNone })}</span>
          ) : null}
        </span>
      </div>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {sections.map((s) => {
          const c = hc.bySection[s] ?? EMPTY;
          return (
            <li key={s} className="flex flex-col gap-1 rounded-2xl border border-[var(--line)] p-3" style={{ borderTop: `5px solid ${SECTION_COLOR[s]}` }}>
              <span className="text-[15px] font-bold text-[var(--ink)]">
                {t(sectionGroupKey(s))}
              </span>
              <span className="text-[26px] leading-none font-bold tabular-nums">{c.yes}</span>
              <span className="text-[14px] text-[var(--muted)]">{t('calendar.headcount.coming_word', { count: c.yes })}</span>
              <span className="text-[13px] text-[var(--muted)] tabular-nums">{t('calendar.headcount.detail', { no: c.no, maybe: c.maybe, none: c.none })}</span>
            </li>
          );
        })}
      </ul>
      {low.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {low.map((s) => {
            const n = hc.bySection[s]?.yes ?? 0;
            const none = hc.bySection[s]?.none ?? 0;
            const section = t(sectionGroupKey(s));
            return (
              <li
                key={s}
                className="flex items-center gap-2 rounded-xl border-l-4 border-[var(--warn)] bg-[color-mix(in_srgb,var(--warn)_14%,transparent)] px-3 py-2 font-semibold text-[var(--ink)]"
              >
                <AlertTriangle className="size-5 shrink-0 text-[var(--warn-text)]" aria-hidden />
                {n === 0 ? t('calendar.headcount.nobody', { section }) : t('calendar.headcount.low', { count: n, section })}
                {none > 0 ? ` · ${t('calendar.headcount.unanswered', { count: none })}` : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Card>
  );
}
